import React, { useState, useEffect } from 'react';
import { Settings, AlertCircle, Check } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

interface SystemConfig {
  id: number;
  config_key: string;
  config_value: string;
  description?: string;
  config_type: string;
  min_value?: number;
  max_value?: number;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at?: string;
}

interface ConfigCardProps {
  config: SystemConfig;
  onUpdate: (key: string, value: string) => Promise<void>;
}

const ConfigCard: React.FC<ConfigCardProps> = ({ config, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newValue, setNewValue] = useState(config.config_value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    try {
      setError(null);
      setIsSaving(true);

      // Validate before saving
      if (config.config_type === 'integer') {
        const val = parseInt(newValue, 10);
        if (isNaN(val)) {
          setError('Value must be a number');
          setIsSaving(false);
          return;
        }
        if (config.min_value !== undefined && val < config.min_value) {
          setError(`Value must be at least ${config.min_value}`);
          setIsSaving(false);
          return;
        }
        if (config.max_value !== undefined && val > config.max_value) {
          setError(`Value must not exceed ${config.max_value}`);
          setIsSaving(false);
          return;
        }
      }

      await onUpdate(config.config_key, newValue);
      setSuccess(true);
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNewValue(config.config_value);
    setIsEditing(false);
    setError(null);
  };

  const isChanged = newValue !== config.config_value;

  return (
    <div className="border rounded-lg p-4 bg-white dark:bg-slate-900 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 dark:text-white">{config.config_key}</h3>
          {config.description && (
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{config.description}</p>
          )}
        </div>
        {success && (
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
            <Check size={16} />
            Saved
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Value {config.config_type === 'integer' && config.min_value !== undefined && (
                <span className="text-xs text-slate-500">({config.min_value}-{config.max_value})</span>
              )}
            </label>
            <input
              type={config.config_type === 'integer' ? 'number' : 'text'}
              value={newValue}
              onChange={(e) => {
                setNewValue(e.target.value);
                setError(null);
              }}
              min={config.min_value}
              max={config.max_value}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md
                         bg-white dark:bg-slate-800 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!isChanged || isSaving}
              className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md
                         hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-700
                         dark:text-slate-300 text-sm font-medium rounded-md hover:bg-slate-50
                         dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div>
            <p className="text-lg font-mono text-slate-700 dark:text-slate-300">{config.config_value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
              Type: {config.config_type}
              {config.min_value !== undefined && ` • Min: ${config.min_value}`}
              {config.max_value !== undefined && ` • Max: ${config.max_value}`}
            </p>
            {config.updated_at && (
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                Last updated: {new Date(config.updated_at).toLocaleDateString()} by {config.updated_by}
              </p>
            )}
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50
                       dark:hover:bg-slate-800 text-sm font-medium rounded-md"
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

export const SystemAdministrationPage: React.FC = () => {
  const [configs, setConfigs] = useState<SystemConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/system-config`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) throw new Error(`Failed to load configs: ${response.statusText}`);
      const data = await response.json();
      // Endpoint returns { data: [...], count: n } — unwrap the array.
      setConfigs(data?.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load system configuration');
      console.error('Failed to load system config:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (key: string, value: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/admin/system-config/${key}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ config_value: value }),
      });
      if (!response.ok) throw new Error(`Failed to update config: ${response.statusText}`);
      // Update local state
      setConfigs(configs.map(c =>
        c.config_key === key
          ? { ...c, config_value: value, updated_at: new Date().toISOString() }
          : c
      ));
    } catch (err) {
      throw err;
    }
  };

  // Keys that have a dedicated editor elsewhere in the app — hidden here so
  // there is exactly one place to manage them.
  // module_link_stoplist: Doc Processing → Metadata Configuration → Module Stoplist.
  const managedElsewhere = new Set(['module_link_stoplist']);
  const listed = configs.filter(c => !managedElsewhere.has(c.config_key));

  // Group configs by section; keys without a named section fall into
  // "Other Settings" so every configuration stays visible and editable.
  const sqlInvestigation = listed.filter(c => c.config_key.startsWith('sql_investigation_'));
  const attachments = listed.filter(c => c.config_key.startsWith('attachment_'));
  const grouped = new Set([...sqlInvestigation, ...attachments].map(c => c.config_key));
  const sections = {
    'SQL Investigation': sqlInvestigation,
    'Attachments': attachments,
    'Other Settings': listed.filter(c => !grouped.has(c.config_key)),
  };

  return (
    // flex-1 + overflow-y-auto: the App shell's content column is
    // overflow-hidden, so the page must own its vertical scroll (same
    // pattern as the Evaluation route).
    <div className="p-6 flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Settings size={28} className="text-slate-700 dark:text-slate-300" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">System Administration</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Configure system-wide operational settings
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mt-3">Loading configuration...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                          rounded-lg p-4 flex items-start gap-3 mb-6">
            <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-200">Error</h3>
              <p className="text-sm text-red-800 dark:text-red-300 mt-1">{error}</p>
              <button
                onClick={loadConfigs}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Try again
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(sections).map(([section, sectionConfigs]) => (
              sectionConfigs.length > 0 && (
                <div key={section}>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    {section}
                  </h2>
                  <div className="space-y-0">
                    {sectionConfigs.map(config => (
                      <ConfigCard
                        key={config.config_key}
                        config={config}
                        onUpdate={handleUpdate}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}

            {listed.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-600 dark:text-slate-400">No system configurations available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAdministrationPage;
